import React, { useState } from 'react';
import { Check, Crown, Headphones, ShieldCheck, Sparkles, CreditCard, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getDefaultWebDevCurrency, getWebDevPlanPrice } from '@/lib/webDevPricing';
import { ServicePlanPriceDisplay } from '@/components/shared/ServicePlanPriceDisplay';

const trustIcons = [CreditCard, XCircle, Headphones];

export function WebDevPlansSection({ language, copy, quoteUrl, onPremiumContact }) {
  const [cycle, setCycle] = useState('monthly');
  const currency = getDefaultWebDevCurrency(language);

  const essentialPrice = getWebDevPlanPrice('essential', currency, cycle, language);
  const growthPrice = getWebDevPlanPrice('growth', currency, cycle, language);
  const enterprisePrice = getWebDevPlanPrice('enterprise', currency, cycle, language);

  return (
    <section id="web-dev-plans" className="max-w-6xl mx-auto mb-20 sm:mb-28 scroll-mt-28 min-w-0 overflow-x-hidden">
      {/* Header */}
      <div className="text-center mb-8 sm:mb-10">
        <div className="inline-flex max-w-full min-w-0 flex-wrap items-center justify-center gap-2 rounded-full border border-sky-500/20 bg-sky-500/[0.06] px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-sky-200/90 mb-4">
          <Sparkles className="w-3.5 h-3.5 shrink-0 text-sky-400" aria-hidden />
          <span className="break-words text-center">{copy.sectionPlansTitle}</span>
        </div>
        <h2 className="text-2xl sm:text-4xl font-black text-white tracking-tight break-words px-1">
          {copy.sectionPlansTitle}
        </h2>
        <p className="text-white/50 mt-3 max-w-2xl mx-auto font-light leading-relaxed break-words">
          {copy.sectionPlansLead}
        </p>
      </div>

      {/* Controls: billing toggle */}
      <div className="flex flex-col items-center gap-4 mb-8 sm:mb-10">
        <div
          className="inline-flex rounded-full border border-white/[0.1] bg-slate-950/60 p-1 shadow-inner shadow-black/40"
          role="group"
          aria-label={`${copy.plansBillingMonthly} / ${copy.plansBillingAnnual}`}
        >
          <button
            type="button"
            onClick={() => setCycle('monthly')}
            className={`relative rounded-full px-5 py-2 text-sm font-semibold transition-colors duration-200 ${
              cycle === 'monthly' ? 'text-slate-950' : 'text-white/55 hover:text-white/80'
            }`}
          >
            {cycle === 'monthly' && (
              <span
                className="absolute inset-0 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 shadow-md shadow-amber-900/25"
                aria-hidden
              />
            )}
            <span className="relative z-10">{copy.plansBillingMonthly}</span>
          </button>
          <button
            type="button"
            onClick={() => setCycle('annual')}
            className={`relative rounded-full px-5 py-2 text-sm font-semibold transition-colors duration-200 ${
              cycle === 'annual' ? 'text-slate-950' : 'text-white/55 hover:text-white/80'
            }`}
          >
            {cycle === 'annual' && (
              <span
                className="absolute inset-0 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 shadow-md shadow-amber-900/25"
                aria-hidden
              />
            )}
            <span className="relative z-10 flex items-center gap-2">
              {copy.plansBillingAnnual}
              {cycle === 'annual' && (
                <span className="hidden sm:inline text-[10px] font-bold uppercase tracking-wide text-emerald-800 bg-emerald-200/90 rounded-full px-2 py-0.5">
                  {copy.plansSaveBadge}
                </span>
              )}
            </span>
          </button>
        </div>

        {cycle === 'annual' && (
          <span className="sm:hidden inline-flex text-[10px] font-bold uppercase tracking-wide text-emerald-300 bg-emerald-500/15 border border-emerald-500/25 rounded-full px-2.5 py-1">
            {copy.plansSaveBadge}
          </span>
        )}
      </div>

      {/* Plan cards */}
      <div className="grid gap-5 lg:gap-6 xl:grid-cols-3 items-stretch min-w-0 lg:py-2">
        {/* Essential */}
        <article className="group relative flex h-full flex-col rounded-[1.75rem] border border-white/[0.1] bg-slate-950/35 backdrop-blur-md p-6 sm:p-7 transition-all duration-300 hover:border-white/20 hover:shadow-[0_20px_60px_-30px_rgba(255,255,255,0.08)]">
          <div
            className="pointer-events-none absolute -inset-px rounded-[1.75rem] opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-white/[0.04] via-transparent to-sky-500/[0.04] blur-xl"
            aria-hidden
          />
          <div className="relative flex flex-col flex-1">
            <h3 className="text-lg font-bold text-white tracking-tight">{copy.planEssentialName}</h3>
            <p className="text-sm text-white/45 font-light mt-1.5 mb-5 leading-relaxed">{copy.planEssentialDesc}</p>
            <ServicePlanPriceDisplay price={essentialPrice} cycle={cycle} />
            <ul className="space-y-2.5 mb-6 flex-1">
              {copy.planEssentialFeatures.map((line) => (
                <li key={line} className="flex gap-2.5 text-sm text-white/65 min-w-0">
                  <Check className="w-4 h-4 shrink-0 text-emerald-400/85 mt-0.5" aria-hidden />
                  <span className="leading-relaxed font-light break-words min-w-0">{line}</span>
                </li>
              ))}
            </ul>
            <Button
              asChild
              variant="outline"
              className="mt-auto rounded-xl w-full border-white/15 bg-white/[0.04] text-white hover:bg-white/10 font-semibold"
            >
              <a href={quoteUrl} target="_blank" rel="noopener noreferrer">
                {copy.planEssentialCta}
              </a>
            </Button>
          </div>
        </article>

        {/* Growth — featured (scale only lg+ to avoid mobile overflow) */}
        <article className="group relative flex h-full flex-col rounded-[1.75rem] border border-indigo-400/40 bg-gradient-to-b from-indigo-500/[0.14] via-slate-950/85 to-slate-950/95 p-6 sm:p-8 shadow-[0_32px_100px_-36px_rgba(99,102,241,0.45)] transition-all duration-300 hover:border-indigo-400/55 ring-1 ring-indigo-500/20 lg:scale-[1.03] lg:origin-center lg:z-10">
          <div
            className="pointer-events-none absolute -inset-px rounded-[1.75rem] opacity-60 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-indigo-500/15 via-purple-500/10 to-sky-500/10 blur-2xl"
            aria-hidden
          />
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-indigo-400/50 bg-slate-950/95 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-indigo-200 shadow-lg shadow-indigo-900/30">
              <Crown className="w-3.5 h-3.5 text-indigo-400" aria-hidden />
              {copy.planGrowthBadge}
            </span>
          </div>
          <div className="relative flex flex-col flex-1 pt-4">
            <h3 className="text-lg font-bold text-white tracking-tight">{copy.planGrowthName}</h3>
            <p className="text-sm text-white/55 font-light mt-1.5 mb-5 leading-relaxed">{copy.planGrowthDesc}</p>
            <ServicePlanPriceDisplay price={growthPrice} cycle={cycle} />
            <ul className="space-y-2.5 mb-6 flex-1">
              {copy.planGrowthFeatures.map((line) => (
                <li key={line} className="flex gap-2.5 text-sm text-white/80 min-w-0">
                  <Check className="w-4 h-4 shrink-0 text-indigo-400/90 mt-0.5" aria-hidden />
                  <span className="leading-relaxed font-light break-words min-w-0">{line}</span>
                </li>
              ))}
            </ul>
            <Button
              asChild
              className="mt-auto rounded-xl w-full bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-semibold shadow-lg shadow-amber-900/25 hover:from-amber-400 hover:to-amber-500 h-11"
            >
              <a href={quoteUrl} target="_blank" rel="noopener noreferrer">
                {copy.planGrowthCta}
              </a>
            </Button>
          </div>
        </article>

        {/* Enterprise */}
        <article className="group relative flex h-full flex-col rounded-[1.75rem] border border-amber-500/25 bg-gradient-to-b from-amber-500/[0.06] via-slate-950/50 to-slate-950/70 p-6 sm:p-8 shadow-[0_20px_70px_-40px_rgba(212,165,116,0.18)] transition-all duration-300 hover:border-amber-500/35">
          <div
            className="pointer-events-none absolute inset-0 rounded-[1.75rem] bg-gradient-to-br from-amber-500/[0.04] via-transparent to-amber-600/[0.03] opacity-90"
            aria-hidden
          />
          <div className="relative flex flex-col flex-1">
            <div className="flex items-center justify-between gap-2 mb-1">
              <h3 className="text-lg font-bold text-white tracking-tight">{copy.planEnterpriseName}</h3>
              <span className="shrink-0 rounded-full border border-amber-500/35 bg-amber-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-200/90">
                {copy.planEnterpriseBadge}
              </span>
            </div>
            <p className="text-sm text-amber-100/45 font-light mt-1 mb-5 leading-relaxed">{copy.planEnterpriseDesc}</p>
            <ServicePlanPriceDisplay
              price={enterprisePrice}
              cycle={cycle}
              priceClassName="text-2xl sm:text-3xl xl:text-4xl font-black text-amber-100/90 tabular-nums tracking-tight leading-tight break-words"
            />
            <ul className="space-y-2.5 mb-6 flex-1">
              {copy.planEnterpriseFeatures.map((line) => (
                <li key={line} className="flex gap-2.5 text-sm text-white/70 min-w-0">
                  <Check className="w-4 h-4 shrink-0 text-amber-400/90 mt-0.5" aria-hidden />
                  <span className="leading-relaxed font-light break-words min-w-0">{line}</span>
                </li>
              ))}
            </ul>
            <Button
              type="button"
              variant="outline"
              className="mt-auto rounded-xl w-full border-amber-500/30 bg-amber-500/[0.08] text-amber-100 hover:bg-amber-500/14 hover:text-amber-50 font-semibold h-11"
              onClick={onPremiumContact}
            >
              {copy.planEnterpriseCta}
            </Button>
          </div>
        </article>
      </div>

      {/* Disclaimer */}
      {copy.plansDisclaimer ? (
        <p className="mt-8 text-xs sm:text-sm text-white/42 text-center max-w-3xl mx-auto leading-relaxed font-light px-4 sm:px-5 rounded-xl border border-white/[0.06] bg-white/[0.025] py-4 sm:py-5">
          {copy.plansDisclaimer}
        </p>
      ) : null}

      {/* Trust line */}
      {copy.plansTrustItems?.length ? (
        <div className="mt-8 pt-8 border-t border-white/[0.06] grid sm:grid-cols-3 gap-4 sm:gap-6">
          {copy.plansTrustItems.map((item, i) => {
            const Icon = trustIcons[i] || ShieldCheck;
            return (
              <div key={item.title} className="flex gap-3 items-start min-w-0 px-1">
                <div className="w-9 h-9 shrink-0 rounded-lg bg-white/[0.04] border border-white/[0.08] flex items-center justify-center">
                  <Icon className="w-4 h-4 text-white/45" aria-hidden />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white/75">{item.title}</p>
                  <p className="text-xs text-white/40 font-light leading-relaxed mt-0.5">{item.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}
